import { distance } from './selection/network';
import type { DevelopmentPlan } from './plans.types';
import type { LngLat, PlanBuilding } from './types';

type FC = GeoJSON.FeatureCollection;

/** Graine stable par plan, pour que chaque plan garde la même grappe de bâtiments. */
const seedOf = (id: string) => [...id].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7);

/**
 * Bâtiments raccordés du plan. Le plan ne stocke que leur nombre : on retient, de façon déterministe,
 * les `buildingCount` bâtiments les plus proches d'un bâtiment de départ propre au plan.
 */
export function planBuildings(plan: DevelopmentPlan, buildings: PlanBuilding[]): PlanBuilding[] {
  if (plan.buildingCount <= 0 || buildings.length === 0) return [];
  const start = buildings[seedOf(plan.id) % buildings.length].centroid;
  return [...buildings]
    .sort((a, b) => distance(start, a.centroid) - distance(start, b.centroid) || a.num - b.num)
    .slice(0, plan.buildingCount);
}

/** Tracé qui relie les bâtiments : arbre couvrant de longueur minimale (Prim) entre leurs centroïdes. */
export function planRoute(buildings: PlanBuilding[]): [LngLat, LngLat][] {
  const pts = buildings.map((b) => b.centroid);
  if (pts.length < 2) return [];
  const best = pts.map((p) => distance(pts[0], p));
  const from = pts.map(() => 0);
  const done = pts.map((_, i) => i === 0);
  const edges: [LngLat, LngLat][] = [];
  for (let k = 1; k < pts.length; k += 1) {
    let next = -1;
    for (let i = 0; i < pts.length; i += 1) if (!done[i] && (next < 0 || best[i] < best[next])) next = i;
    done[next] = true;
    edges.push([pts[from[next]], pts[next]]);
    for (let i = 0; i < pts.length; i += 1) {
      const d = distance(pts[next], pts[i]);
      if (!done[i] && d < best[i]) { best[i] = d; from[i] = next; }
    }
  }
  return edges;
}

export function routeFC(edges: [LngLat, LngLat][]): FC {
  return {
    type: 'FeatureCollection',
    features: edges.map((e, i) => ({ type: 'Feature', id: i + 1, properties: { id: i + 1 }, geometry: { type: 'LineString', coordinates: e } })),
  };
}
